import { useState } from 'react';
import { Button, Loader } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import { generateWordDoc } from '../../../../services/proposalsRoutes';
import { USERFEEDBACKACTIONS } from '../../reducers/userFeedbackReducer';
import { Proposal } from '../../../../interfaces/types';
import { FeeOption } from '../../../../interfaces/fees';
import { useProposal } from '../../context/ProposalContext';

export default function GenerateWordDoc() {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const { proposal, userFeedback, dispatchUserFeedback } = useProposal();
  const { documentLoading } = userFeedback;
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const handleCancelGenerate = () => {
    if (abortController) {
      abortController.abort();
      dispatchUserFeedback({
        type: USERFEEDBACKACTIONS.SET_FIELD,
        field: 'documentLoading',
        value: false,
      });
      setAbortController(null);
    }
  };

  const handleWordDocGenerate = async () => {
    if (documentLoading) return;

    const abortCtrl = new AbortController();
    setAbortController(abortCtrl);

    const proposalForWord: Proposal = {
      ...proposal,
      last_edited: new Date(),
    };

    try {
      const accessToken = await getAccessTokenSilently();
      await generateWordDoc(
        accessToken,
        proposalForWord,
        (loading: boolean) =>
          dispatchUserFeedback({
            type: USERFEEDBACKACTIONS.SET_FIELD,
            field: 'documentLoading',
            value: loading,
          }),
        abortCtrl.signal
      );
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Document generation aborted');
      } else {
        console.error('Error generating Word document:', error);
        alert('Failed to generate Word document.');
      }
    } finally {
      setAbortController(null);
    }
  };

  return (
    <Button
      color={documentLoading ? 'red' : 'blue'}
      variant="light"
      leftSection={documentLoading ? <Loader size="xs" color="blue" /> : <IconPlus size={18} />}
      onClick={documentLoading ? handleCancelGenerate : handleWordDocGenerate}
      size="md"
    >
      {documentLoading ? t('cancel') : t('generateWordDoc')}
    </Button>
  );
}
